import { blendGrade, gradeForWorld, type Grade } from './art/grading';

/* ═══════════════════════════════════════════════════════════════════════════
   L'ambiance courante — l'état d'étalonnage de la vue.

   Pourquoi un singleton de module plutôt qu'une dépendance passée :

   L'étalonnage est décidé par le MONDE (`View` le lit dans `World.run.levelId`)
   et consommé par la CHAÎNE DE POST (`VfxPipeline`). Ces deux objets sont
   construits séparément par le point d'entrée et ne se connaissent pas — le
   premier reçoit le `Stage`, le second le renderer et les scènes. Les câbler
   demanderait de rouvrir `main.ts` alors que l'ambiance n'est pas une décision
   d'application : c'est un état INTERNE à la vue, dérivé du monde, au même titre
   que la position d'un vaisseau.

   Le singleton est donc la formulation honnête de ce qui est vrai : il n'existe
   qu'une seule vue, donc une seule ambiance.

   ⚠️ La transition est LENTE et lissée. Un changement instantané de LUT au
   franchissement d'un monde produirait un saut de couleur d'une frame que l'œil
   lit comme un défaut d'affichage. Deux secondes de fondu, sur un moment où le
   joueur vient de voir l'écran de cartes, et le monde a changé de peau sans que
   personne ait vu la couture.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Durée du fondu entre deux mondes, en secondes. */
const FADE_SEC = 2.0;

export class WorldGrade {
  private from = 1;
  private to = 1;
  private t = 1;
  private dirty = true;
  private blended: Grade = gradeForWorld(1);
  /** Mode de compensation du daltonisme (une valeur de `Colorblind`). Il ne
   *  change pas l'étalonnage `blended` — il est appliqué EN AVAL, à l'écriture de
   *  la LUT — mais un changement doit forcer une réécriture, d'où le `dirty`. */
  private cb = 0;

  /** Appelé par `View.sync`, à chaque frame. Ne fait rien si le monde n'a pas
   *  changé — c'est un chemin de rendu, il ne doit rien allouer ni recalculer. */
  setWorld(worldNo: number): void {
    const n = worldNo < 1 ? 1 : worldNo;
    if (n === this.to) return;
    /* On repart de l'ambiance RÉELLEMENT affichée, pas de `to` : franchir deux
       mondes pendant un fondu (possible en debug, et un jour en speedrun)
       produirait sinon un retour en arrière visible avant de repartir. */
    this.from = this.t >= 1 ? this.to : this.from;
    this.to = n;
    this.t = 0;
    this.dirty = true;
  }

  /** Avance le fondu. Retourne `true` si la table doit être réécrite. */
  advance(dtSec: number): boolean {
    if (this.t < 1) {
      this.t = Math.min(1, this.t + dtSec / FADE_SEC);
      this.dirty = true;
    }
    if (!this.dirty) return false;
    this.dirty = false;
    this.blended =
      this.t >= 1
        ? gradeForWorld(this.to)
        : blendGrade(gradeForWorld(this.from), gradeForWorld(this.to), this.t);
    return true;
  }

  /** L'étalonnage à écrire dans la LUT. Valide seulement après `advance()`. */
  get current(): Grade {
    return this.blended;
  }

  get worldNo(): number {
    return this.to;
  }

  /** Mode de daltonisme courant, lu par `writeLut`. */
  get colorblind(): number {
    return this.cb;
  }

  /** Change la compensation du daltonisme et force la prochaine `advance()` à
   *  réécrire la LUT. No-op si le mode est inchangé — c'est un chemin appelé
   *  depuis les options, pas par frame, mais autant ne rien salir pour rien. */
  setColorblind(mode: number): void {
    if (mode === this.cb) return;
    this.cb = mode;
    this.dirty = true;
  }

  /** Remet l'ambiance au monde 1 sans fondu. Sert à la reprise après une perte
   *  de contexte : reconstruire une LUT en fondu depuis un état perdu n'aurait
   *  aucun sens. */
  reset(): void {
    this.from = 1;
    this.to = 1;
    this.t = 1;
    this.dirty = true;
  }
}

/** L'unique instance. */
export const worldGrade = new WorldGrade();
